import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GrAttachment } from "react-icons/gr";
import { Link, useParams } from "react-router-dom";
import { Anchor, Group, LoadingOverlay, Stack, Text } from "@mantine/core";
import { useDisclosure, useMounted } from "@mantine/hooks";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { GeneralTabProps } from "@pages/Patients/Laboratory/components/GeneralTab";

import { CustomPagination } from "@components/CustomPagination";
import { FilePreviewModal } from "@components/FilePreviewModal";

import { formatDateToString } from "@utils/dateValidation";

import { QP } from "@constants/index";

import { useGetPrescriptionsForPatient } from "@hooks/queries/prescription/useGetPrescriptionsForPatient";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { useAuth } from "@contexts/auth";

import { Prescription } from "@customTypes/prescription";

import { StyledTable } from "@styles/components/Table.styled";


const columnHelper = createColumnHelper<Partial<Prescription>>();

export const FastTab = ({ setIsRefreshing }: GeneralTabProps) => {
  const { t, i18n } = useTranslation();

  const { patientId } = useParams();
  const { token } = useAuth();
  const { tablePagination, setTablePagination } = useSyncTableQueryParams({});

  const [tableData, setTableData] = useState<Partial<Prescription>[] | []>([]);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedRow, setSelectedRow] = useState<Partial<Prescription> | null>(
    null
  );

  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetPrescriptionsForPatient({
      patientId: patientId as string,
      fastConsultationOnly: true,
      pagination: {
        page: tablePagination.page,
        pageSize: tablePagination.pageSize,
      },
    });

  const fastPrescriptionColumns = useMemo(
    () => [
      columnHelper.accessor("consultationInfo.cId", {
        header: t("consultation"),
        cell: info => {
          return (
            <Group gap={8} align="start">
              <span className="appear">
                <GrAttachment color="var(--clr-accent)" />
              </span>

              <Stack gap={5}>
                <Text fz={14} c="black">
                  <Anchor
                    fz={14}
                    component={Link}
                    c={"accent.4"}
                    to={`/patients/${patientId}/consultation/general/${info.row.original.consultationId}`}
                    className={"hover-underline"}
                    td="none"
                    ff={"var(--font-secondary)"}
                    fw={450}>
                    {info.cell.getValue()}
                  </Anchor>
                </Text>
                <Text c="dimmed" fz={13}>
                  {formatDateToString(
                    info.row.original.consultationInfo?.date as Date
                  )}
                  {", "}
                  {new Date(
                    info.row.original.consultationInfo?.date as Date
                  ).toLocaleTimeString(i18n.language, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
              </Stack>
            </Group>
          );
        },
      }),
      columnHelper.accessor("date", {
        header: t("date-created"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(info.cell.getValue() as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {(info.cell.getValue() as Date).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor("prescribedBy", {
        header: t("uploaded-by"),
        cell: info => info.cell.getValue()?.fullName,
      }),
    ],
    [tableData, i18n.language]
  );

  const table = useReactTable({
    data: tableData,
    columns: fastPrescriptionColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <>
      <LoadingOverlay
        visible={isRefetching && !!data?.newData?.length && !isMounted}
        zIndex={1000}
        overlayProps={{ blur: 0.1, opacity: 0.6 }}
        loaderProps={{ display: "none" }}
      />
      <div className="table-wrapper">
        <StyledTable $hasPointerCursorOnHover>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {isSuccess && !data?.newData?.length ? (
            <tbody>
              <tr className="helper-row">
                <td colSpan={fastPrescriptionColumns.length}>
                  <p>{t("empty-prescriptions")}</p>
                </td>
              </tr>
            </tbody>
          ) : null}

          {isLoading && fetchStatus === "fetching" && !tableData.length ? (
            <tbody>
              <tr className="helper-row">
                <td colSpan={fastPrescriptionColumns.length}>
                  <p>{t("loading")}</p>
                </td>
              </tr>
            </tbody>
          ) : null}

          {isError && !data?.newData?.length ? (
            <tbody>
              <tr className="helper-row">
                <td colSpan={fastPrescriptionColumns.length}>
                  <p>{t("encountered-error")}</p>
                </td>
              </tr>
            </tbody>
          ) : null}

          <FilePreviewModal
            opened={opened}
            close={close}
            docs={
              selectedRow?.files
                ? [
                    ...selectedRow.files.map(item => ({
                      uri: "https://" + item + `?token=${token}`,
                      fileName: (item as string).split("/files/")[1],
                    })),
                  ]
                : []
            }
          />
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                onClick={() => {
                  setSelectedRow(row.original);
                  open();
                }}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </div>

      {(isLoading && fetchStatus === "fetching") || !data ? null : (
        <CustomPagination
          table={table}
          tablePagination={tablePagination}
          otherParams={{ [QP.TAB]: "fast" }}
        />
      )}
    </>
  );
};
